import React, { useState, useMemo, useCallback, useRef, useEffect } from "react";
import {
    DefaultButton,
    PrimaryButton,
    SearchBox,
    Callout,
    DirectionalHint,
    Checkbox
} from "@fluentui/react";
import { Box, Collapse, IconButton } from "@mui/material";
import { ChevronRight20Regular, ChevronDown20Regular, Filter20Regular, Dismiss16Regular } from "@fluentui/react-icons";

interface HierarchicalProductFilterProps {
    /** Mapping of ProductFamily to its Products */
    productHierarchy: Record<string, string[]>;
    /** Currently selected products (can be family names or product names) */
    selectedProducts: string[];
    /** Callback when selection changes */
    onSelectionChange: (products: string[]) => void;
}

// Theme colors
const THEME = {
    primary: "#0078D4",
    primaryLight: "#E7F1FF",
    border: "#E1E5EB",
    textPrimary: "#323130",
    textSecondary: "#605E5C"
};

// Consistent text styles matching Fluent UI
const textStyles = {
    fontFamily: "'Segoe UI', 'Segoe UI Web (West European)', -apple-system, BlinkMacSystemFont, Roboto, 'Helvetica Neue', sans-serif",
    header: {
        fontSize: 14,
        fontWeight: 600,
        color: THEME.textPrimary
    },
    family: {
        fontSize: 14,
        fontWeight: 400,
        color: THEME.textPrimary
    },
    product: {
        fontSize: 14,
        fontWeight: 400,
        color: THEME.textPrimary
    }
};

export const HierarchicalProductFilter: React.FC<HierarchicalProductFilterProps> = ({
    productHierarchy,
    selectedProducts,
    onSelectionChange
}) => {
    const [isOpen, setIsOpen] = useState(false);
    const [searchText, setSearchText] = useState("");
    const [expandedFamilies, setExpandedFamilies] = useState<Set<string>>(new Set());
    const [tempSelection, setTempSelection] = useState<Set<string>>(new Set(selectedProducts));
    const buttonRef = useRef<HTMLDivElement>(null);

    // Sync temp selection when selectedProducts changes
    useEffect(() => {
        setTempSelection(new Set(selectedProducts));
    }, [selectedProducts]);

    // Get all products for a family
    const getProductsForFamily = useCallback((family: string): string[] => {
        return productHierarchy[family] || [];
    }, [productHierarchy]);

    // Check if a family is fully selected (all products selected)
    const isFamilyFullySelected = useCallback((family: string): boolean => {
        const products = getProductsForFamily(family);
        return products.length > 0 && products.every(p => tempSelection.has(p));
    }, [getProductsForFamily, tempSelection]);

    // Check if a family is partially selected (some products selected)
    const isFamilyPartiallySelected = useCallback((family: string): boolean => {
        const products = getProductsForFamily(family);
        const selectedCount = products.filter(p => tempSelection.has(p)).length;
        return selectedCount > 0 && selectedCount < products.length;
    }, [getProductsForFamily, tempSelection]);

    // Toggle family expansion
    const toggleFamilyExpand = useCallback((family: string, e: React.MouseEvent) => {
        e.stopPropagation();
        setExpandedFamilies(prev => {
            const newSet = new Set(prev);
            if (newSet.has(family)) {
                newSet.delete(family);
            } else {
                newSet.add(family);
            }
            return newSet;
        });
    }, []);

    // Toggle family selection (select/deselect all products in family)
    const toggleFamilySelection = useCallback((family: string) => {
        const products = getProductsForFamily(family);
        const isFullySelected = isFamilyFullySelected(family);

        setTempSelection(prev => {
            const newSet = new Set(prev);
            if (isFullySelected) {
                // Deselect all products in this family
                products.forEach(p => newSet.delete(p));
            } else {
                // Select all products in this family
                products.forEach(p => newSet.add(p));
            }
            return newSet;
        });
    }, [getProductsForFamily, isFamilyFullySelected]);

    // Toggle individual product selection
    const toggleProductSelection = useCallback((product: string) => {
        setTempSelection(prev => {
            const newSet = new Set(prev);
            if (newSet.has(product)) {
                newSet.delete(product);
            } else {
                newSet.add(product);
            }
            return newSet;
        });
    }, []);

    // Toggle "All" selection
    const toggleAll = useCallback(() => {
        const allProducts = Object.values(productHierarchy).flat();
        const allSelected = allProducts.length > 0 && allProducts.every(p => tempSelection.has(p));

        if (allSelected) {
            setTempSelection(new Set());
        } else {
            setTempSelection(new Set(allProducts));
        }
    }, [productHierarchy, tempSelection]);

    // Check if all products are selected
    const isAllSelected = useMemo(() => {
        const allProducts = Object.values(productHierarchy).flat();
        return allProducts.length > 0 && allProducts.every(p => tempSelection.has(p));
    }, [productHierarchy, tempSelection]);

    // Filter families and products based on search
    const filteredHierarchy = useMemo(() => {
        if (!searchText) return productHierarchy;

        const lowerSearch = searchText.toLowerCase();
        const filtered: Record<string, string[]> = {};

        Object.entries(productHierarchy).forEach(([family, products]) => {
            // Check if family name matches
            const familyMatches = family.toLowerCase().includes(lowerSearch);
            // Filter products that match
            const matchingProducts = products.filter(p => p.toLowerCase().includes(lowerSearch));

            if (familyMatches || matchingProducts.length > 0) {
                filtered[family] = familyMatches ? products : matchingProducts;
            }
        });

        return filtered;
    }, [productHierarchy, searchText]);

    // Handle Apply
    const handleApply = useCallback(() => {
        onSelectionChange(Array.from(tempSelection));
        setIsOpen(false);
    }, [tempSelection, onSelectionChange]);

    // Handle Cancel
    const handleCancel = useCallback(() => {
        setTempSelection(new Set(selectedProducts));
        setIsOpen(false);
    }, [selectedProducts]);

    // Get display text for button
    const displayText = useMemo(() => {
        if (selectedProducts.length === 0) {
            return "All Products";
        }
        const allProducts = Object.values(productHierarchy).flat();
        if (selectedProducts.length === allProducts.length) {
            return "All Products";
        }
        if (selectedProducts.length === 1) {
            return selectedProducts[0];
        }
        return `${selectedProducts.length} products`;
    }, [selectedProducts, productHierarchy]);

    return (
        <>
            <div ref={buttonRef}>
                <Box
                    onClick={() => setIsOpen(!isOpen)}
                    sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 1,
                        px: 1.5,
                        py: 0.75,
                        minWidth: 180,
                        height: 36,
                        backgroundColor: selectedProducts.length > 0 ? THEME.primaryLight : "#FFFFFF",
                        borderRadius: "6px",
                        border: `1px solid ${selectedProducts.length > 0 ? THEME.primary : THEME.border}`,
                        cursor: "pointer",
                        fontFamily: textStyles.fontFamily,
                        "&:hover": {
                            borderColor: THEME.primary,
                            backgroundColor: THEME.primaryLight
                        }
                    }}
                >
                    <Filter20Regular style={{ color: THEME.primary, fontSize: 16 }} />
                    <span
                        style={{
                            fontSize: 13,
                            fontWeight: 600,
                            color: THEME.primary,
                            flex: 1,
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                            fontFamily: textStyles.fontFamily
                        }}
                    >
                        Product: {displayText}
                    </span>
                    {selectedProducts.length > 0 && (
                        <IconButton
                            size="small"
                            onClick={(e) => {
                                e.stopPropagation();
                                onSelectionChange([]);
                            }}
                            sx={{ p: 0.25, ml: 0.5 }}
                        >
                            <Dismiss16Regular />
                        </IconButton>
                    )}
                </Box>
            </div>

            {isOpen && (
                <Callout
                    target={buttonRef.current}
                    onDismiss={handleCancel}
                    directionalHint={DirectionalHint.bottomLeftEdge}
                    isBeakVisible={false}
                    styles={{
                        root: { marginTop: 4 },
                        calloutMain: {
                            width: 300,
                            maxHeight: 450,
                            overflow: "hidden",
                            borderRadius: 8,
                            boxShadow: "0 4px 16px rgba(0,0,0,0.14)",
                            fontFamily: textStyles.fontFamily
                        }
                    }}
                >
                    {/* Header with search */}
                    <Box sx={{ p: 1.5, borderBottom: `1px solid ${THEME.border}` }}>
                        <span style={{ ...textStyles.header, display: "block", marginBottom: 8 }}>
                            Product
                        </span>
                        <SearchBox
                            placeholder="Search..."
                            value={searchText}
                            onChange={(_, newValue) => setSearchText(newValue || "")}
                            onClear={() => setSearchText("")}
                            styles={{
                                root: { width: "100%" }
                            }}
                        />
                    </Box>

                    {/* Scrollable list */}
                    <Box sx={{ maxHeight: 280, overflowY: "auto", py: 0.5 }}>
                        {/* All option - removed since it doesn't make sense with hierarchical filter */}

                        {/* Product families with expandable products */}
                        {Object.entries(filteredHierarchy).map(([family, products]) => {
                            const isExpanded = expandedFamilies.has(family);
                            const isFullySelected = isFamilyFullySelected(family);
                            const isPartiallySelected = isFamilyPartiallySelected(family);

                            return (
                                <Box key={family}>
                                    {/* Family row */}
                                    <Box
                                        sx={{
                                            display: "flex",
                                            alignItems: "center",
                                            px: 0.5,
                                            py: 0.25,
                                            cursor: "pointer",
                                            "&:hover": { backgroundColor: "#F3F2F1" }
                                        }}
                                    >
                                        {/* Expand/Collapse button */}
                                        <IconButton
                                            size="small"
                                            onClick={(e) => toggleFamilyExpand(family, e)}
                                            sx={{ p: 0.25, mr: 0 }}
                                        >
                                            {isExpanded ? (
                                                <ChevronDown20Regular style={{ fontSize: 16, color: THEME.textSecondary }} />
                                            ) : (
                                                <ChevronRight20Regular style={{ fontSize: 16, color: THEME.textSecondary }} />
                                            )}
                                        </IconButton>

                                        {/* Family checkbox and label */}
                                        <Checkbox
                                            checked={isFullySelected}
                                            indeterminate={isPartiallySelected}
                                            label={family}
                                            onChange={() => toggleFamilySelection(family)}
                                            styles={{
                                                root: { flex: 1 },
                                                checkbox: {
                                                    width: 16,
                                                    height: 16,
                                                    borderRadius: 2
                                                },
                                                label: {
                                                    fontFamily: textStyles.fontFamily,
                                                    fontSize: 14,
                                                    fontWeight: 400,
                                                    color: THEME.textPrimary,
                                                    marginLeft: 4
                                                }
                                            }}
                                        />
                                    </Box>

                                    {/* Products (collapsible) */}
                                    <Collapse in={isExpanded}>
                                        <Box sx={{ pl: 3.5 }}>
                                            {products.map((product) => (
                                                <Box
                                                    key={product}
                                                    sx={{
                                                        display: "flex",
                                                        alignItems: "center",
                                                        px: 1,
                                                        py: 0.25,
                                                        cursor: "pointer",
                                                        "&:hover": { backgroundColor: "#F3F2F1" }
                                                    }}
                                                >
                                                    <Checkbox
                                                        checked={tempSelection.has(product)}
                                                        label={product}
                                                        onChange={() => toggleProductSelection(product)}
                                                        styles={{
                                                            root: { flex: 1 },
                                                            checkbox: {
                                                                width: 16,
                                                                height: 16,
                                                                borderRadius: 2
                                                            },
                                                            label: {
                                                                fontFamily: textStyles.fontFamily,
                                                                fontSize: 14,
                                                                fontWeight: 400,
                                                                color: THEME.textPrimary,
                                                                marginLeft: 4
                                                            }
                                                        }}
                                                    />
                                                </Box>
                                            ))}
                                        </Box>
                                    </Collapse>
                                </Box>
                            );
                        })}
                    </Box>

                    {/* Footer with Apply/Cancel */}
                    <Box
                        sx={{
                            display: "flex",
                            gap: 1,
                            p: 1.5,
                            borderTop: `1px solid ${THEME.border}`,
                            backgroundColor: "#FAFAFA"
                        }}
                    >
                        <PrimaryButton text="Apply" onClick={handleApply} styles={{ root: { flex: 1 } }} />
                        <DefaultButton text="Cancel" onClick={handleCancel} styles={{ root: { flex: 1 } }} />
                    </Box>
                </Callout>
            )}
        </>
    );
};

export default HierarchicalProductFilter;
